import { SmlsButton, SmlsInputText } from "@smiles/smiles-ui-kit-react";
import React, { useEffect, useState } from "react";
import { Helpers } from "@smiles/core-smiles";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IconInformationCircle } from "../../assets/IconInformationCircle";
import { TangInputPassword } from "../../components/TangInputPassword";
import { init } from "../../../services/core/config";
import { ILoginTypes, validateDocumentStatusV2 } from "../../../services/core/services";
import { useLoginModalFlow, LoginModal } from "../../context/LoginModalFlowContext";
import useLoginTries from "../../hooks/useLoginTries";
import { getLink } from "../../../utils";
import { setRedirectEnvironmentInLocalStorage } from "../../utils/redirectEnviroment";
import { EventIdentifierMethod, trackLoginEvents } from "../../utils/eventTracking";

const FIRST_ACCESS_STATUS = [
  'INCOMPL. REGISTRATION PARTNER',
  'PENDING ENROLLMENT',
  'PRE REGISTRATION BANKS',
];

type ValidType = 'EMAIL' | 'CPF' | 'MEMBER';

interface WebAuthProps {
  code: string;
}

export function LeftSidebarNoRecaptcha() {
  const databaseConnection = 'smilesdb';

  const { t } = useTranslation('acesso');

  const [searchParams] = useSearchParams();
  const sourceAccess = searchParams.get('src')?.toLowerCase() || 'smiles';
  const sourceIsGolBrand = sourceAccess === 'gol';
  const automationBypass = !!searchParams.get('bpr');

  const authClient = searchParams.get('client') || '';

  useEffect(() => {
    setRedirectEnvironmentInLocalStorage({
      sourceAccess,
      authClient
    })
  }, [searchParams]);

  const loginModalFlow = useLoginModalFlow();
  const loginTries = useLoginTries();

  const [isWifiLogin, setIsWifiLogin] = useState<boolean>(false);

  console.log('[debug] isWifiLogin', isWifiLogin);

  const [identifier, setIdentifier] = useState<string>("");
  const [identifierType, setIdentifierType] = useState<string>('');
  const [pass, setPass] = useState<string>("");

  const [isContinueButtonLoading, setIsContinueButtonLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [passwordClear, setPasswordClear] = useState<boolean>(false);

  const [isPasswordInputVisible, setIsPasswordInputVisible] = useState<boolean>(false);
  const [isContinueButtonVisible, setIsContinueButtonVisible] = useState<boolean>(true);
  const [isSubmitButtonVisible, setIsSubmitButtonVisible] = useState<boolean>(false);

  const hasPass = !!pass && pass.length >= 4;

  const canContinue = (!!identifier && !!identifierType);

  console.log('canContinue', canContinue, identifier, identifierType)
  const canSubmit = !!identifier && !!identifierType && hasPass;

  const [identifierErrorMessage, setIdentifierErrorMessage] = useState<string>("");
  const [hasIdentifierError, setHasIdentifierError] = useState<boolean>(false);

  const errorMessages: Record<ValidType, string> = {
    EMAIL: t('smiles_01_a_04.input_errors.email'),
    MEMBER: t('smiles_01_a_04.input_errors.member'),
    CPF: t('smiles_01_a_04.input_errors.document'),
  }

  const handleIdentifierErrorMessage = (type: ValidType): string => {
    return errorMessages[type];
  };

  useEffect(() => {
    if(hasIdentifierError) {
      setIdentifierErrorMessage(handleIdentifierErrorMessage(identifierType as ValidType));
    }
  }, [t, hasIdentifierError, identifierType])

  useEffect(() => {
    init();

    try {
      const decodedString = new TextDecoder().decode(Uint8Array.from(atob(window.auth0config), c => c.charCodeAt(0)));
      const config = JSON.parse(decodedString);

      const isWifi = config.dict.signin.title === 'spa-uv-wifi';
      setIsWifiLogin(isWifi);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('fail to get config: ', error);
    }
  }, []);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    console.log('Submit Event', event);
    throw new Error('sample');
  };

  const handleIdentifierChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    setIdentifier(input);
    setHasIdentifierError(false);
    disabledPasswordInput();
  }

  const handleIdentifierValidate = (inputText: string): boolean => {
    let input = inputText;

    if (!Number.isNaN(Number(input.replace(/[.-]+/g, '')))) {
      input = inputText.replace(/[.-]+/g, '');
    }

    const validation = Helpers.validations.isValidLogin(input);

    const { isValid, ...rest } = validation;

    const identifierMask = rest?.mask || input;

    const type = validation.type || '';

    setIdentifierType(type);

    if (isValid) {
      trackLoginEvents.loginMethod(type as EventIdentifierMethod);
      setIdentifier(identifierMask);
    }

    const hasError = !isValid && !!type;

    if (hasError) {
      setHasIdentifierError(true);
    }

    return !hasError;
  }

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    setPass(input);

    if(input && input.length >= 4) {
      trackLoginEvents.passwordInput();
    }
  }

  const allowPasswordInput = () => {
    setIsContinueButtonLoading(false);
    setIsContinueButtonVisible(false);
    setIsSubmitButtonVisible(true);
    setIsPasswordInputVisible(true);
  }

  const disabledPasswordInput = () => {
    setIsContinueButtonVisible(true);
    setIsSubmitButtonVisible(false);
    setIsPasswordInputVisible(false);
  }

  const handleContinueButton = async () => {
    setIsContinueButtonLoading(true);

    trackLoginEvents.recaptchaContinue();

    let memberIdentifier = identifier;

    if (identifierType === 'CPF') {
      memberIdentifier = memberIdentifier.replace(/[.-]+/g, '')
    }

    try {
      const response = await validateDocumentStatusV2({
        type: identifierType as ILoginTypes,
        value: memberIdentifier,
      });

      console.log('[validateDocumentStatus] response', response);
    
      if (!response.data || !response.success) {
        const isBlockAkamai = response.errors.flow.incidentAkamai;
        const flowModalIfError = isBlockAkamai ? LoginModal.BLOCK_AKAMAI_INPUT_USER : LoginModal.ACCOUNT_NOT_FOUND;
        loginModalFlow.openModal(flowModalIfError);
        trackLoginEvents.AccountNotFound();
        setIsPasswordInputVisible(false);
        setIsContinueButtonLoading(false);
        loginTries.setTries(0);
        return;
      }

      const passwordTries = response.data.countPasswordLock || 0;
      loginTries.setTries(passwordTries);

      if(passwordTries > 4) {
        loginModalFlow.openModal(LoginModal.PASSWORD_BLOCKED);
        setIsPasswordInputVisible(false);
        setIsContinueButtonLoading(false);
        return;
      }

      const memberData = response.data;

      const isCaduLogin = !memberData?.lastLogin && !memberData.acceptTerm && memberData.first && memberData.sourceBrand === 1;

      if(isCaduLogin) {
        loginModalFlow.openModal(LoginModal.FIRST_ACCESS);
        setIsContinueButtonLoading(false);
        return;
      }

      const preRegistration = {
        openedSr: response.data.openedSr || false,
        realStatus: response.data.realStatus || '',
      }

      const isPreRegistration = preRegistration.openedSr && ['INCOMPL. REGISTRATION PARTNER', 'PENDING ENROLLMENT'].includes(preRegistration.realStatus);

      if (isPreRegistration) {
        loginModalFlow.openModal(LoginModal.ACCOUNT_UNDER_REVIEW);
        setIsPasswordInputVisible(false);
        setIsContinueButtonLoading(false);
        return;
      }

      const isFirstAccess = !response.data?.lastLogin && FIRST_ACCESS_STATUS.includes(preRegistration.realStatus);

      if (isFirstAccess) {
        loginModalFlow.openModal(LoginModal.PRE_REDO_REGISTER, () => {
          allowPasswordInput();
        });

        setIsPasswordInputVisible(false);
        setIsContinueButtonLoading(false);
        return;
      }

      allowPasswordInput();
    } catch (err) {
      setIsContinueButtonLoading(false);
      console.log('[validateDocumentStatus] An error has occurred', err);
    }
  }

  const handleSubmitButton = () => {
    setIsSubmitting(true);

    trackLoginEvents.loginContinue();

    let memberIdentifier = identifier;

    if (identifierType === 'CPF') {
      memberIdentifier = memberIdentifier.replace(/[.-]+/g, '')
    }

    window.webAuth.login({
      realm: databaseConnection,
      username: memberIdentifier,
      password: pass,
      connection_scope: 'http://auth0.com/oauth/grant-type/password-realm',
    }, (err: WebAuthProps) => {
      console.log(err);

      if (String(err.code).startsWith('invalid_user_password')) {
        const updatedTries = loginTries.tries + 1;
        loginTries.increaseTries();

        if(updatedTries >= 5) {
          setIdentifier('');
        }

        if([0, 1, 2, 3].includes(updatedTries)) {
          loginModalFlow.openModal(LoginModal.INCORRECT_DATA);
          trackLoginEvents.IncorrectData();
        } else if (updatedTries === 4) {
          loginModalFlow.openModal(LoginModal.PASSWORD_LAST_CHANCE);
          trackLoginEvents.ModalLastTry();
        } else {
          loginModalFlow.openModal(LoginModal.PASSWORD_BLOCKED);
          trackLoginEvents.ModalBlockPassword();
          setIsPasswordInputVisible(false);
        }
      } else {
        loginModalFlow.openModal(LoginModal.ERROR_GENERIC);
      }

      setPass('');
      setPasswordClear(!passwordClear);
      setIsSubmitting(false);
    })
  }

  const handleForgotPasswordClick = () => {
    trackLoginEvents.forgotPassword();
    window.location.href = getLink('recuperar/senha');
  }

  const handleCreateAccountClick = () => {
    window.location.href = getLink('cadastro');
  }

  return (
    <div className="left-sidebar-container">
      <div className="left-box-wrapper">
        <h2 className="title">{t('smiles_01_a_04.title')}</h2>
        <p className="description">{t('smiles_01_a_04.text')}</p>
        <form onSubmit={handleSubmit}>
          <div className="identifier-input">
            <SmlsInputText
              inputMode="text"
              id="identifier"
              textLabel={t('smiles_01_a_04.input')}
              placeholder=""
              value={identifier}
              debouncedTime={700}
              onChange={handleIdentifierChange}
              validate={handleIdentifierValidate}
              errorMessage={identifierErrorMessage}
            />
          </div>

          {isPasswordInputVisible && (
            <div className="password-input">
              <TangInputPassword onlyNumbers textLabel={t('smiles_05.input')} onChange={handlePasswordChange} flipFlopClear={passwordClear} />
            </div>
          )}

          {
            sourceIsGolBrand && isContinueButtonVisible && (
              <div className="warning-gol-origin">
                <IconInformationCircle />
                <p>{t('gol_01_a_03.text')}</p>
              </div>
            )
          }

          {
            sourceIsGolBrand && isSubmitButtonVisible && (
              <div className="warning-gol-origin">
                <IconInformationCircle />
                <p>
                  {t('gol_04_a_05.text')}
                </p>
              </div>
            )
          }

          <div className="bottom-buttons">
            <SmlsButton text={t('smiles_01_a_04.button_secondary')} color="hyperlink" onClick={handleForgotPasswordClick} />

            {isContinueButtonVisible &&
              <SmlsButton disabled={!canContinue} isLoading={isContinueButtonLoading} text={t('smiles_01_a_04.button_primary')} onClick={handleContinueButton} />}
            {isSubmitButtonVisible &&
              <SmlsButton disabled={!canSubmit} text={t('smiles_01_a_04.button_primary')} isLoading={isSubmitting} onClick={handleSubmitButton} />}
          </div>
        </form>
      </div>
      <div className="footer-create-account">
        <p>{t('smiles_01_a_04.text2')}</p>
        <SmlsButton text={t('smiles_01_a_04.button_secondary2')} color="hyperlink" onClick={handleCreateAccountClick} />
      </div>
    </div>
  )
}